import axios from '../helpers/axios'
import { logError } from '../services';
import {
  IDropdownResponse,
  IChangeNeighborhoodSeriesPayload,
  IIncentiveTypesRes,
  IIncentiveTypeUpdate,
  IAddressBookUser,
  IAddressBookUserUpdate,
  IAddressBookUserDetails,
  IAddressBookLookup,
  ISalesToolsEmailTemplatesRes,
  IResponse,
  ISalesAgentReport,
  IPartAbbreviationsRes,
  IPartAbbreviationUpdate
} from '../models';

const baseURL = '/api/sales/tools';

export const getAgentNeighborhoods = async () => {
  try {
    const { data } = await axios.get(`${baseURL}/agent-neighborhoods`);
    return data;
  } catch (error) {
    logError(error, 'getAgentNeighborhoods failed');
    return Promise.reject(error);
  }
};

export const getLotsForNeighborhood = async (
  neighborhoodId: number | string
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseURL}/job-lots/${neighborhoodId}`);

    return data;
  } catch (error: any) {
    logError(error, 'getJobLots failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodSeriesForJob = async (jobId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/neighborhood-series/${jobId}`);

    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesForJob failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putChangeNeighborhoodSeries = async (
  jobId: number | string,
  payload: IChangeNeighborhoodSeriesPayload
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/neighborhood-series/${jobId}`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putChangeNeighborhoodSeries failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getIncentiveTypes = async (filters?: {
  name?: string;
  isDeleted?: Boolean;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
}): Promise<IIncentiveTypesRes> => {
  try {
    const { data } = await axios.get(`${baseURL}/incentive-types`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getIncentiveTypes failed');
    return Promise.reject(error?.response?.data);
  }
};

export const addIncentiveType = async (post: IIncentiveTypeUpdate) => {
  try {
    const { data } = await axios.post(`${baseURL}/incentive-types`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { post } }, 'AddIncentiveType failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updateIncentiveType = async (
  id: number,
  payload: IIncentiveTypeUpdate
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/incentive-types/${id}`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: { id, payload } }, 'updateIncentiveType failed');
    return Promise.reject(error);
  }
};

export const deleteIncentiveType = async (id: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`${baseURL}/incentive-types/${id}`);
    return data;
  } catch (error) {
    logError(error, 'deleteIncentiveType failed');
    return Promise.reject(error);
  }
};

// get current contacts from address book //

export const getAddressBook = async (filters?: {
  name?: string;
  isGlobal?: boolean;
  neighborhoodIds?: number[];
  territoryIds?: number[];
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number;
}): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseURL}/address-book`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getAddressBook failed');
    return Promise.reject(error?.response?.data);
  }
};

// add contact in address book //

export const addAddressBookUser = async (user: IAddressBookUser): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseURL}/address-book`, user);
    return data;
  } catch (error) {
    logError({ error, payload: { user } }, 'addAddressBookUser failed');
    return Promise.reject(error?.response?.data);
  }
};

// remove contact from address book //

export const deleteAddressBookUser = async (id: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`${baseURL}/address-book/${id}`);
    return data;
  } catch (error) {
    logError(error, 'deleteAddressBookUser failed');
    return Promise.reject(error?.response?.data);
  }
};

// get contact from address book //

export const getAddressBookUser = async (id: number): Promise<IAddressBookUserDetails> => {
  try {
    const { data } = await axios.get(`${baseURL}/address-book/${id}`);
    return data;
  } catch (error) {
    logError(error, 'getAddressBookUser failed');
    return Promise.reject(error?.response?.data);
  }
};

// update contact in address book //

export const updateAddressBookUser = async (
  id: number,
  user: IAddressBookUserUpdate
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/address-book/${id}`, user);
    return data;
  } catch (error) {
    logError({ error, payload: { id, user } }, 'updateAddressBookUser failed');
    return Promise.reject(error);
  }
};

// Gets users that can be added to the address book //

export const getAddressBookLookup = async (): Promise<IAddressBookLookup[]> => {
  try {
    const { data } = await axios.get(`${baseURL}/address-book/lookup`);
    return data;
  } catch (error) {
    logError(error, 'getAddressBookLookup failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSalesToolsEmailTemplates = async (filters?: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
}): Promise<ISalesToolsEmailTemplatesRes> => {
  try {
    const { data } = await axios.get(`${baseURL}/email-templates`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getSalesToolsEmailTemplates failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSalesAgentReport = async (filters?: {
  dateFrom?: string;
  dateTo?: string;
  territoryId?: string | number;
  neighborhoodId?: string | number;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number;
}): Promise<IResponse<ISalesAgentReport[]>> => {
  try {
    const { data } = await axios.get(`${baseURL}/reports/sales-agent-report`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getSalesAgentReport failed');
    return Promise.reject(error);
  }
};

export const getPartAbbreviations = async (filters?: {
  search?: string;
  isDeleted?: Boolean;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  perPage?: number | string;
}): Promise<IPartAbbreviationsRes> => {
  try {
    const { data } = await axios.get(`${baseURL}/part-abbreviations`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPartAbbreviations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const addPartAbbreviation = async (post: IPartAbbreviationUpdate) => {
  try {
    const { data } = await axios.post(`${baseURL}/part-abbreviations`, post);
    return data;
  } catch (error) {
    logError({ error, payload: { post } }, 'addPartAbbreviations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updatePartAbbreviation = async (
  id: number,
  payload: IPartAbbreviationUpdate
): Promise<any> => {
  try {
    const { data } = await axios.put(`${baseURL}/part-abbreviations/${id}`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: { id, payload } }, 'updatePartAbbreviations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const deletePartAbbreviations = async (id: number): Promise<any> => {
  try {
    const { data } = await axios.delete(`${baseURL}/part-abbreviations/${id}`);
    return data;
  } catch (error) {
    logError(error, 'deletePartAbbreviations failed');
    return Promise.reject(error?.response?.data);
  }
};
